import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  whyNotReschedulable,
  localWallTimeUtc,
  type DispatchQueue,
} from '@servwell/shared';
import { TimelineBoard, type TimelineLane } from '../schedule/TimelineBoard';
import { dayPins } from '../schedule/boardPins';
import { DispatchPanes, MapPane } from '../schedule/DispatchPanes';
import { JobPreviewPanel, type PreviewTarget } from '../schedule/JobPreviewPanel';
import { JobQueues } from '../schedule/JobQueues';
import { CrewMapTab, type CrewMapJob } from '../schedule/CrewMapTab';
import { visitsLine } from '../schedule/crewFacts';
import { shareEqualDeep } from '../../lib/shareEqualDeep';
import { RigPanel, type Rig } from '../schedule/RigPanel';
import { WeekBoard } from '../schedule/WeekBoard';
import { WeekPanel, type PanelVisit, type WeekPanelTarget } from '../schedule/WeekPanel';
import {
  UNASSIGNED_LANE,
  buildWeekGrid,
  weekDays,
  weekStart,
  type WeekLane,
} from '../schedule/weekGrid';
import { UnderlineTabs, UnderlineTabList, UnderlineTab } from '../ui/Tabs';
import { SCHEDULE_TABS, useUrlTab } from '../ui/pageTabs';
import { useTRPC } from '../../lib/trpc';
import {
  addDaysISO,
  dateOnly,
  dayLabel,
  dayRange,
  daysBetweenISO,
  todayISO,
} from '../../lib/format';
import { idem } from '../../lib/idempotency';
import { toggleRow } from '@/components/ui/dataTableSelection';
import { minutesInTz } from '../../lib/format';
import { PageRoot, PageHeader, Toolbar } from '../ui/Page';
import { Button } from '../ui/Button';
import { StatusBadge, Notice } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
  worstError,
  worstState,
} from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { IconSchedule, IconChevronLeft, IconChevronRight } from '../icons';
import { WithOrgClock } from '../ui/WithOrgClock';

type Assignee = { userId: string; name: string; isLead: boolean };
type BoardRow = {
  visit: {
    id: string;
    status: string;
    scheduledStartAt: string | Date;
    scheduledEndAt: string | Date | null;
  };
  job: { id: string; jobNumber: number; title: string; status: string; priority: string };
  customer: { id: string; displayName: string; phone: string | null };
  property: {
    id: string;
    label: string;
    address1: string | null;
    city: string | null;
    /* The map pane's rows are the BOARD's rows — one query, so a pin and a card can never
       disagree about which visits exist today (113/A5). */
    latitude: number | null;
    longitude: number | null;
    geocodeStatus: string | null;
  };
  assignees: Assignee[];
};

const UNASSIGNED = '__unassigned__';

/**
 * "MA, DB" — who is on a visit, for a lane whose crew is bigger than one.
 *
 * ⚠️ Falls back to the whole name rather than an empty string when a name has no letters to
 * take. A blank chip on a two-tech card says "nobody", which is the opposite of the fact it
 * is there to carry.
 */
function initialsOf(assignees: { name: string }[]): string | null {
  const marks = assignees
    .map((a) => {
      const parts = a.name.trim().split(/\s+/).filter(Boolean);
      const letters = parts
        .map((w) => w[0])
        .join('')
        .toUpperCase();
      return letters || a.name.trim();
    })
    .filter(Boolean);
  return marks.length > 0 ? marks.join(', ') : null;
}

/**
 * A crew map job as the preview panel's target: the map's opener, beside the board card's.
 *
 * ⚠️ `crew.day` hands out no phone and no address line (its allow-list, spec §1). So both are
 * `undefined`, "not carried", and the panel leaves those rows out: a dash (null) would say the
 * shop has none, and a city in the Address row would pass for an address. The city rides with
 * the property's label instead. The page does not fetch them back for the panel.
 */
function crewTarget(j: CrewMapJob): PreviewTarget {
  return {
    key: j.visit.id,
    jobId: j.job.id,
    visitId: j.visit.id,
    kind: 'visit',
    ref: `#${j.job.jobNumber}`,
    title: j.job.title,
    status: j.visit.status,
    customer: j.customer.displayName,
    phone: undefined,
    propertyLabel:
      [j.property.label, j.property.city].filter(Boolean).join(' · ') || null,
    address: undefined,
    assignees: j.assignees.map((a) => a.name),
    startAt: j.visit.scheduledStartAt,
    endAt: j.visit.scheduledEndAt,
    stuck: whyNotReschedulable(j.visit.status),
  };
}

/**
 * Dispatch board — the interactive one. v1's board was a read-only feed even though
 * `visit.reschedule` existed server-side (build audit's headline failure); here every visit
 * can be moved by drag between days or by keyboard from its panel.
 *
 * And moved in BULK, which is the shape the actual bad day has. It rains, and eleven
 * outdoor jobs need to be Thursday's problem — done one at a time that is eleven drags,
 * eleven round trips, and eleven chances to lose count. Every move on this page, single or
 * bulk, goes through one mutation (`visit.rescheduleMany`) so there is one behaviour to
 * understand and one place where the day arithmetic lives: the server, in the shop's
 * timezone, where "same time, Thursday" survives a daylight-saving change.
 */
export function SchedulePage() {
  return (
    <WithOrgClock what="the schedule" icon={IconSchedule} title="Schedule">
      {(tz) => <ScheduleBoard tz={tz} />}
    </WithOrgClock>
  );
}

/**
 * ⭐ Split out so `tz` is a `string` before the first `useState` runs. The day this board
 * opens on IS the shop's today, so the zone is needed in the initialiser — there is no
 * point later in the component where a conditional could supply it.
 */
function ScheduleBoard({ tz }: { tz: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [dayIso, setDayIso] = useState(todayISO(tz));
  const [targetIso, setTargetIso] = useState(addDaysISO(todayISO(tz), 1));
  /**
   * What the preview panel is showing — the ONE selection all three panes share (113/A5).
   *
   * ⚠️ A `PreviewTarget`, not a `BoardRow`, because two of its three openers cannot produce
   * a board row: a map pin can (it is a board row), but a queue row is a job or a request
   * that may have no visit at all. Keying the panel on the board's shape would have made
   * the bottom pane unable to open it.
   */
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const [queue, setQueue] = useState<DispatchQueue>('unassigned');
  /** The rig drill-down (113/A1b): the tech, their van, and the kit on it. */
  const [rig, setRig] = useState<Rig | null>(null);
  const [mapOpen, setMapOpen] = useState(true);
  const [dragId, setDragId] = useState<string | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());

  const range = dayRange(dayIso, tz);
  const board = useQuery(trpc.visit.board.queryOptions(range));
  /* The LANES, from the server (114 part 2). Was `org.members` filtered in this file by
     an inline role list — the board's own private opinion about who can be sent to a job,
     while both assignee pickers offered the same list unfiltered. `visit.rigs` answers it
     once, and carries the van each tech is in ON THE VIEWED DAY, which a roster of members
     cannot know. */
  const rigs = useQuery(trpc.visit.rigs.queryOptions({ date: dayIso }));

  /** Day | Week | Map, in the URL (105). The default never appears in a link the app writes. */
  const [tab, setTab] = useUrlTab(SCHEDULE_TABS);
  const [weekTarget, setWeekTarget] = useState<WeekPanelTarget | null>(null);

  /**
   * The Map tab is mounted the first time it is opened, then kept (ops insights Phase 4).
   *
   * ⚠️ Not before: Leaflet measures its box when the map is made, and a map made inside a
   * `hidden` block is measured at 0×0 for nothing. Kept after because `DispatchMap` is
   * create-once: unmounting would destroy the Leaflet map and its tiles, and the next visit
   * would build both again. It does NOT keep the reader's pan and zoom: coming back from the
   * hidden 0×0 box is a resize, and the map's ResizeObserver refits to the pins.
   * Recorded during render (React's "information from previous renders" pattern), so the
   * first render on `?tab=map` already mounts it.
   */
  const [mapSeen, setMapSeen] = useState(tab === 'map');
  if (tab === 'map' && !mapSeen) setMapSeen(true);
  const mapActive = tab === 'map';
  /**
   * The crew map's picked tech (a chip or a face), or null for everyone (ops insights Phase 4,
   * ticket 03). Page state, beside `preview`, so the right panel (ticket 04) switches on the
   * same value the map dims by. Picking never refits the map.
   */
  const [crewPick, setCrewPick] = useState<string | null>(null);
  /**
   * The crew map's selected job (ticket 04): a pin, a not-on-the-map row, or a row of the
   * right panel. The pin is ringed and the panel's row marked, both ways. A row of the panel
   * selects WITHOUT opening the preview ("Open job" does that); a pin opens the preview too,
   * as it always has. Selecting never refits the map.
   */
  const [crewSelected, setCrewSelected] = useState<string | null>(null);
  /**
   * The crew map's one read [Q2, Q4]. No input: it is always the shop's today, so moving the
   * Day board to tomorrow does not move the map.
   *
   * ⚠️ It keeps itself current only while it is LOOKED AT: every 60 s while the Map tab shows
   * and the browser tab is VISIBLE (`refetchIntervalInBackground: false` defers to TanStack's
   * focus manager, which watches `visibilitychange`: a visible window that is not focused
   * still refreshes), and again when the browser tab becomes visible.
   *
   * 🔴 `enabled` is `mapActive`, not `mapSeen`. Once opened the map stays MOUNTED on Day and
   * Week, and an enabled query refetches on every broad invalidation (each move on the board
   * invalidates everything) and on reconnect. Disabled while hidden, it waits, stale, until
   * the Map tab shows again.
   */
  const crew = useQuery(
    trpc.crew.day.queryOptions(undefined, {
      enabled: mapActive,
      refetchInterval: mapActive ? 60_000 : false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: mapActive ? 'always' : false,
      // A refresh that changed nothing keeps every row's identity (its dates included), so
      // the map is handed the same jobs and techs and redraws nothing.
      structuralSharing: shareEqualDeep,
    }),
  );

  /**
   * The WEEK's own two reads (121).
   *
   * ⚠️ A separate `visit.board` window rather than reusing the day's — a week is the same
   * procedure with a wider range, and asking for both means the day board keeps its own
   * cheap single-day query instead of filtering a week down on every render.
   *
   * ⚠️ `rigsForRange`, not seven `rigs` calls. The van is a per-DAY fact, and seven
   * independent reads can disagree about who exists — a lane present on Tuesday and absent
   * on Wednesday is a hole in a grid rather than a fact about anyone.
   */
  const weekFrom = weekStart(dayIso);
  const weekTo = weekDays(weekFrom)[6]!;
  const weekBoard = useQuery(
    trpc.visit.board.queryOptions({
      from: dayRange(weekFrom, tz).from,
      to: dayRange(weekTo, tz).to,
    }),
  );
  const weekRigs = useQuery(
    trpc.visit.rigsForRange.queryOptions({ from: weekFrom, to: weekTo }),
  );

  /**
   * Drag to an exact TIME (114 part 3). The bulk `rescheduleMany` below takes whole DAYS,
   * which is why the board could move a job to Thursday and never to 2pm.
   *
   * ⚠️ `scheduledEndAt` is deliberately NOT sent. The service preserves the visit's length
   * when the end is omitted — a fix that shipped in PR #45, where `?? null` had been
   * DELETING the duration on exactly this call.
   */
  const moveToTime = useMutation(
    trpc.visit.reschedule.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  const move = useMutation(
    trpc.visit.rescheduleMany.mutationOptions({
      onSuccess: () => {
        // The selection is spent — the visits it named are on another day now, and the
        // ones that weren't moved are listed by name below with the reason.
        setPicked(new Set());
        setPreview(null);
        void qc.invalidateQueries();
      },
    }),
  );

  /**
   * Changing day retires the selection.
   *
   * Non-negotiable: ticks belong to visits you can SEE. Carrying them to Wednesday's board
   * would leave the owner one click from moving three of Tuesday's jobs while looking at a
   * screen that shows neither them nor their checkmarks.
   */
  const goToDay = (iso: string) => {
    setDayIso(iso);
    setTargetIso(addDaysISO(iso, 1));
    setPicked(new Set());
    move.reset();
  };

  /**
   * 🔴 BOTH queries, and the second one was gated nowhere.
   *
   * `board` decided the page; `rigs` decides the LANES. While the member list was
   * loading or failed, `techs` was empty, so every per-tech lane disappeared and only
   * Unassigned drew — and the visits assigned to those techs were not reported as "none",
   * they were not reported at all. A dispatch board quietly missing today's assigned work
   * is the worst shape in this ticket: nothing on screen says anything is absent.
   */
  const state = worstState(shownState(board, board.data), shownState(rigs, rigs.data));
  // A whole PAGE that is merely loading keeps its full-page skeleton — collapsing it to a
  // single 24-unit bar under the header was a regression this contract introduced by
  // treating a page like a card.
  /*
   * ⚠️ Both early returns are the DAY board's, and the Map tab reads neither query: it draws
   * from `crew.day` and says its own loading and failure. On Map they must not replace it, so
   * a slow or failed board leaves the map standing (and the hidden Day block draws nothing).
   */
  if (state === 'loading' && tab !== 'map') return <PageSkeleton />;
  if ((!board.data || !rigs.data) && tab !== 'map') {
    return (
      <PageRoot>
        <PageHeader title="Schedule" />
        <UnresolvedState
          state={unresolved(state)}
          what="the schedule"
          icon={IconSchedule}
          // `??` takes the FIRST error, not the one that matters: a retained 500 on
          // `board` hides a 403 on `rigs` behind a Try-again that can never fully
          // succeed. A refusal has to win — see `worstError`.
          error={worstError(board.error, rigs.error)}
          onRetry={() => {
            void board.refetch();
            void rigs.refetch();
          }}
        />
      </PageRoot>
    );
  }

  // Empty only on the Map tab, past the Day board's gate above (its block is hidden there).
  const rows = (board.data ?? []) as BoardRow[];
  /*
   * ⭐ The lanes come from the SERVER, already filtered. This file used to hold the rule
   * itself — `m.role === 'tech' || m.role === 'owner'`, inline — while both assignee
   * pickers offered `org.members` UNFILTERED, so a bookkeeper could be assigned a visit
   * from a creation sheet and land on a board with no lane to draw them in. `visit.rigs`
   * applies `canHoldVisits` in the query, the same predicate `visit.setAssignees` refuses
   * the write with. The page no longer has an opinion to drift.
   */
  const techs = rigs.data ?? [];

  /**
   * One lane per tech, plus an Unassigned lane that always shows — an unassigned visit is
   * the single most important thing on this screen.
   *
   * ⚠️ `minutesInTz` resolves each visit against the SHOP's clock, not the browser's. A
   * dispatcher on a laptop in Denver would otherwise see a Boston shop's 9am job sitting
   * two columns to the left.
   */
  const toVisit = (r: BoardRow, crewSize: number) => ({
    id: r.visit.id,
    start: minutesInTz(r.visit.scheduledStartAt, tz),
    end: r.visit.scheduledEndAt ? minutesInTz(r.visit.scheduledEndAt, tz) : null,
    status: r.visit.status,
    title: r.job.title,
    customer: r.customer.displayName,
    address: r.property.address1,
    startAt: r.visit.scheduledStartAt,
    endAt: r.visit.scheduledEndAt,
    // Initials only when the lane's crew is bigger than one — otherwise the card would
    // repeat the lane header on every row (113 decision 2).
    initials: crewSize > 1 ? initialsOf(r.assignees) : null,
    stuck: whyNotReschedulable(r.visit.status),
  });

  const lanes: TimelineLane[] = [
    {
      key: UNASSIGNED,
      name: 'Unassigned',
      unassigned: true,
      visits: rows.filter((r) => r.assignees.length === 0).map((r) => toVisit(r, 1)),
    },
    ...techs.map((t) => ({
      key: t.userId,
      name: t.name ?? t.email ?? 'Tech',
      /* The van travelling with them on THIS day, or the absence said out loud. A tech
         with work and no van is a thing the office has to see, not a row to quietly drop. */
      van: t.vehicle
        ? [t.vehicle.name, t.vehicle.plate].filter(Boolean).join(' · ')
        : null,
      visits: rows
        .filter((r) => r.assignees.some((a) => a.userId === t.userId))
        .map((r) => toVisit(r, r.assignees.length)),
    })),
  ];

  /**
   * A board row as the preview panel's target — the FIRST of A5's three openers, and the
   * one the map pin shares (a pin's id IS a visit id, so a pin opens this too).
   */
  const boardTarget = (r: BoardRow): PreviewTarget => ({
    key: r.visit.id,
    jobId: r.job.id,
    visitId: r.visit.id,
    kind: 'visit',
    ref: `#${r.job.jobNumber}`,
    title: r.job.title,
    status: r.visit.status,
    customer: r.customer.displayName,
    phone: r.customer.phone,
    propertyLabel: r.property.label,
    address: [r.property.address1, r.property.city].filter(Boolean).join(', ') || null,
    assignees: r.assignees.map((a) => a.name),
    startAt: r.visit.scheduledStartAt,
    endAt: r.visit.scheduledEndAt,
    stuck: whyNotReschedulable(r.visit.status),
  });

  /**
   * ⚠️ Opening one right-hand panel CLOSES the other. Both are `Sheet`s on the right edge,
   * so two open at once is two overlapping drawers with one visible and the other's focus
   * trap still live — the reader would tab into a panel they cannot see.
   */
  const openPreview = (target: PreviewTarget | null) => {
    setRig(null);
    setPreview(target);
  };
  const openVisit = (visitId: string) => {
    const row = rows.find((r) => r.visit.id === visitId);
    openPreview(row ? boardTarget(row) : null);
  };
  const openRig = (next: Rig) => {
    setPreview(null);
    setRig(next);
  };

  /**
   * The map's pins, from the SAME rows the lanes draw.
   *
   * ⚠️ Not memoised, and deliberately not: every hook in this component runs before the
   * loading and error returns above, and `rows` does not exist until after them. A `useMemo`
   * here would be a hook after a conditional return — the one thing React forbids outright.
   * The work is a sort and a single pass over one day of visits; the day this is hot, the
   * board above it will have been unreadable for a long time.
   */
  const { pins, unplaced } = dayPins(
    rows.map((r) => ({
      id: r.visit.id,
      status: r.visit.status,
      title: r.job.title,
      customer: r.customer.displayName,
      address: r.property.address1,
      latitude: r.property.latitude,
      longitude: r.property.longitude,
      geocodeStatus: r.property.geocodeStatus,
      // On the SHOP's clock, so the pin numbers run in the order the day actually happens
      // rather than in the order the reader's laptop thinks it does.
      startMinute: minutesInTz(r.visit.scheduledStartAt, tz),
    })),
  );

  // Only what can actually move is selectable, so a rain-day sweep doesn't quietly fill up
  // with jobs a tech already started and come back half-refused.
  const movable = rows.filter((r) => !whyNotReschedulable(r.visit.status));
  const allPicked = movable.length > 0 && picked.size === movable.length;

  const send = (visitIds: string[], shiftDays: number) => {
    if (visitIds.length === 0 || shiftDays === 0) return;
    move.mutate({ visitIds, shiftDays, ...idem() });
  };

  const toggle = (visitId: string) => setPicked((prev) => toggleRow(prev, visitId));

  /** Dragging a card that's part of the selection moves the whole selection, not just it. */
  const onDropDay = (dropIso: string) => {
    const id = dragId;
    setDragId(null);
    if (!id) return;
    send(picked.has(id) ? [...picked] : [id], daysBetweenISO(dayIso, dropIso));
  };

  /**
   * The week grid, and the visits the panel can describe.
   *
   * ⚠️ The Unassigned lane is FIRST and is not a person (decision 2) — a week's worth of
   * unbooked work in one glance instead of seven.
   */
  const weekRows = (weekBoard.data ?? []) as BoardRow[];
  const weekLanes: WeekLane[] = [
    { laneId: UNASSIGNED_LANE, userId: null, name: 'Unassigned', vanless: false },
    ...(weekRigs.data ?? []).map((r) => ({
      laneId: r.userId,
      userId: r.userId,
      name: r.name ?? r.email ?? 'Unnamed',
      vanless: r.vanless,
    })),
  ];
  const weekGrid = buildWeekGrid(
    weekRows.map((r) => ({
      id: r.visit.id,
      scheduledStartAt: r.visit.scheduledStartAt,
      scheduledEndAt: r.visit.scheduledEndAt,
      assigneeUserIds: r.assignees.map((a) => a.userId),
    })),
    weekLanes,
    weekDays(weekFrom),
    tz,
  );
  const weekVisitsById = new Map<string, PanelVisit>(
    weekRows.map((r) => [
      r.visit.id,
      {
        id: r.visit.id,
        status: r.visit.status,
        scheduledStartAt: r.visit.scheduledStartAt,
        jobTitle: r.job.title,
        customerName: r.customer.displayName,
        address:
          [r.property.address1, r.property.city].filter(Boolean).join(', ') || null,
      },
    ]),
  );

  return (
    <PageRoot>
      <PageHeader
        title="Schedule"
        /*
         * ⚠️ The subtitle describes WHAT IS ON SCREEN. It read "0 visits · Today" while the
         * week board below it showed two stops — the day's count under the week's grid,
         * which is a header arguing with its own page. Found by looking at the render.
         */
        sub={
          tab === 'week'
            ? `${weekRows.length} ${weekRows.length === 1 ? 'visit' : 'visits'} · week of ${dateOnly(weekFrom, { day: 'numeric', month: 'short' })}`
            : tab === 'map'
              ? // The MAP's day, which is the shop's today whatever day the board is on.
                crew.data
                ? // Counted as the panel's All and the Everyone chip count: canceled apart.
                  `${visitsLine(crew.data.jobs)} · ${dayLabel(crew.data.date, tz)}`
                : 'Today'
              : `${rows.length} ${rows.length === 1 ? 'visit' : 'visits'} · ${dayLabel(dayIso, tz)}`
        }
        right={
          move.isPending ? (
            <span className="text-sm text-muted-foreground">Saving…</span>
          ) : null
        }
      />

      {/* The boards' notice: the Map tab draws `crew.day`, which says its own age. */}
      <StaleNotice
        show={
          tab !== 'map' &&
          (refreshFailed(board, board.data) || refreshFailed(rigs, rigs.data))
        }
        onRetry={() => {
          void board.refetch();
          void rigs.refetch();
        }}
      />

      {/*
        Day | Week — ONE route, the tab in the URL (105's `useUrlTab`, the idiom `/fleet`
        and `/today` already use). A dispatcher who lives in Week stops being sent back to
        Day every morning, and a link can point at either.

        🔴 **Both boards stay MOUNTED**, hidden with `hidden` rather than unmounted. This is
        105's `keepMounted` lesson in the form this page needs: the day board holds a live
        multi-select and a drag in progress, and a tab flip that threw those away would lose
        a rain-day sweep somebody was halfway through picking.
      */}
      <UnderlineTabs value={tab} onValueChange={setTab}>
        <UnderlineTabList>
          <UnderlineTab value="day">Day</UnderlineTab>
          <UnderlineTab value="week">Week</UnderlineTab>
          <UnderlineTab value="map">Map</UnderlineTab>
        </UnderlineTabList>
      </UnderlineTabs>

      <div className={tab === 'day' ? 'contents' : 'hidden'}>
        <Toolbar>
          <Button
            variant="ghost"
            onClick={() => goToDay(addDaysISO(dayIso, -1))}
            aria-label="Previous day"
          >
            <IconChevronLeft size={16} />
          </Button>
          <Button variant="ghost" onClick={() => goToDay(todayISO(tz))}>
            Today
          </Button>
          <Button
            variant="ghost"
            onClick={() => goToDay(addDaysISO(dayIso, 1))}
            aria-label="Next day"
          >
            <IconChevronRight size={16} />
          </Button>
          <span className="ml-1 font-heading text-sm font-semibold">
            {dayLabel(dayIso, tz)}
          </span>
          {movable.length > 0 ? (
            <label className="ml-auto flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-[var(--sw-brand)]"
                checked={allPicked}
                onChange={() =>
                  setPicked(
                    allPicked ? new Set() : new Set(movable.map((r) => r.visit.id)),
                  )
                }
              />
              Select the whole day
            </label>
          ) : null}
        </Toolbar>

        {/*
        Inline and full-width rather than floating over the board: a bar that hovers above
        the lanes covers the very cards you're picking from.
      */}
        {picked.size > 0 ? (
          <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 rounded-[var(--radius)] border border-brand bg-surface p-3 shadow-sm">
            <span className="text-sm font-semibold">
              {picked.size} {picked.size === 1 ? 'visit' : 'visits'} selected
            </span>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              Move to
              <input
                type="date"
                value={targetIso}
                onChange={(e) => setTargetIso(e.target.value)}
                className="rounded-[var(--radius-sm)] border border-border bg-surface px-2 py-1 text-sm text-[var(--sw-text)]"
              />
            </label>
            <Button
              disabled={move.isPending || daysBetweenISO(dayIso, targetIso) === 0}
              onClick={() => send([...picked], daysBetweenISO(dayIso, targetIso))}
            >
              {move.isPending ? 'Moving…' : `Move to ${dayLabel(targetIso, tz)}`}
            </Button>
            <Button variant="ghost" onClick={() => setPicked(new Set())}>
              Clear
            </Button>
            {daysBetweenISO(dayIso, targetIso) === 0 ? (
              <span className="text-xs text-muted-foreground">
                That&apos;s the day they&apos;re already on — pick another.
              </span>
            ) : null}
          </div>
        ) : null}

        {move.isError ? (
          <Notice tone="danger">
            Couldn&apos;t move {picked.size > 1 ? 'those visits' : 'that visit'} —{' '}
            {move.error.message}
          </Notice>
        ) : null}

        {/*
        The honest half, and the reason a bulk move is allowed to be partial at all. A tech
        can start a job while the office is planning around it; that's a normal Tuesday. What
        would NOT be acceptable is moving six of eight and saying nothing — so anything left
        behind is named here, with the reason, until the next move or a change of day.
      */}
        {move.data && move.data.skipped.length > 0 ? (
          <section className="rounded-[var(--radius)] border border-border bg-surface p-4">
            <h2 className="text-sm font-semibold">
              Moved {move.data.moved.length} · left {move.data.skipped.length} where{' '}
              {move.data.skipped.length === 1 ? 'it was' : 'they were'}
            </h2>
            <ul className="mt-2 space-y-1 text-sm">
              {move.data.skipped.map((s) => (
                <li key={s.visitId} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {s.jobNumber ? `#${s.jobNumber} ` : ''}
                    {s.customerName ?? 'A visit'}
                  </span>
                  <StatusBadge tone="neutral">{s.why}</StatusBadge>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Drop targets for moving a visit to the previous/next day without leaving the view. */}
        <div className="grid grid-cols-2 gap-3">
          {[-1, 1].map((delta) => {
            const iso = addDaysISO(dayIso, delta);
            const carrying = dragId && picked.has(dragId) ? picked.size : 1;
            return (
              <div
                key={delta}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropDay(iso)}
                className={`rounded-[var(--radius)] border border-dashed p-2 text-center text-xs ${
                  dragId
                    ? 'border-brand text-[var(--sw-brand-text)]'
                    : 'border-border text-muted-foreground'
                }`}
              >
                {dragId
                  ? `Drop to move ${carrying > 1 ? `${carrying} visits` : 'it'} to ${dayLabel(iso, tz)}`
                  : dayLabel(iso, tz)}
              </div>
            );
          })}
        </div>

        {rows.length === 0 ? (
          <EmptyState
            icon={IconSchedule}
            title={`Nothing scheduled for ${dayLabel(dayIso, tz).toLowerCase()}`}
            description="Visits you schedule on a job show up here, in the assigned tech's lane."
          />
        ) : (
          <DispatchPanes
            board={
              <TimelineBoard
                lanes={lanes}
                tz={tz}
                /* The now-line's ONLY condition: the day on screen is the shop's today.
                 Drawing it on Thursday's board while it is Tuesday would be a red line
                 through a day that has not happened. */
                isToday={dayIso === todayISO(tz)}
                nowMinute={minutesInTz(new Date(), tz)}
                selected={picked}
                openId={preview?.visitId ?? null}
                onToggleSelect={toggle}
                onOpen={openVisit}
                onOpenRig={(laneKey) => {
                  const t = techs.find((x) => x.userId === laneKey);
                  if (t) {
                    openRig({
                      userId: t.userId,
                      name: t.name ?? t.email ?? 'Tech',
                      vehicle: t.vehicle,
                    });
                  }
                }}
                onDropAt={(id, minute) => {
                  const row = rows.find((r) => r.visit.id === id);
                  if (!row || whyNotReschedulable(row.visit.status)) return;
                  /**
                   * ⚠️ `localWallTimeUtc`, NOT local-midnight-plus-minutes. The client is not
                   * doing timezone arithmetic here — it is asking the shared resolver for the
                   * instant at which the dropped column reads on the SHOP's clock. Midnight
                   * plus elapsed minutes assumes one offset all day and is an hour wrong on
                   * both DST days, which would make a dropped card jump a column on exactly
                   * the two days a dispatcher is least able to explain it.
                   */
                  const at = localWallTimeUtc(dayIso, minute, tz);
                  moveToTime.mutate({ visitId: id, scheduledStartAt: at, ...idem() });
                }}
              />
            }
            map={
              <MapPane
                open={mapOpen}
                onToggle={() => setMapOpen((v) => !v)}
                pins={pins}
                unplaced={unplaced}
                selectedId={preview?.visitId ?? null}
                onSelect={openVisit}
              />
            }
          />
        )}

        {/*
         * The bottom pane. It is NOT a filter over the day above — read `JobQueues`' own
         * docstring. It sits outside the `rows.length === 0` branch on purpose: an empty
         * Tuesday is exactly when the office most wants to see what is waiting to be booked.
         */}
        <JobQueues queue={queue} onQueueChange={setQueue} tz={tz} onOpen={openPreview} />
      </div>

      <div className={tab === 'week' ? 'contents' : 'hidden'}>
        <WeekBoard
          grid={weekGrid}
          todayIso={todayISO(tz)}
          onOpenLane={(laneId) => setWeekTarget({ level: 'week', laneId })}
          onOpenCell={(laneId, day) => setWeekTarget({ level: 'day', laneId, day })}
        />
      </div>

      {/*
        The crew map. Mounted once first opened (`mapSeen`), then kept and hidden like the
        other two. It has no toolbar: no day stepper, no Today button. It is today, always.
      */}
      {mapSeen ? (
        <div className={mapActive ? 'contents' : 'hidden'}>
          <CrewMapTab
            active={mapActive}
            crew={crew}
            tz={tz}
            selectedId={crewSelected}
            onSelect={setCrewSelected}
            onOpen={(job) => {
              setCrewSelected(job.visit.id);
              openPreview(crewTarget(job));
            }}
            picked={crewPick}
            onPick={setCrewPick}
          />
        </div>
      ) : null}

      <WeekPanel
        target={weekTarget}
        grid={weekGrid}
        visitsById={weekVisitsById}
        tz={tz}
        onClose={() => setWeekTarget(null)}
        onNavigate={setWeekTarget}
        onOpenDayBoard={(day) => {
          // 104's footer contract: the panel is a transient preview, and its full-width
          // action goes to the URL-real page. The day board is DEMOTED here, not lost.
          setWeekTarget(null);
          setTab('day');
          goToDay(day);
        }}
      />

      <RigPanel rig={rig} dayIso={dayIso} onClose={() => setRig(null)} />

      <JobPreviewPanel
        target={preview}
        tz={tz}
        onClose={() => {
          setPreview(null);
          // The crew map's selection was the preview's: closing it lets go of both.
          setCrewSelected(null);
        }}
        onMoveDays={(visitId, days) => send([visitId], days)}
        moving={move.isPending}
      />
    </PageRoot>
  );
}
